import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, unref as _unref, normalizeClass as _normalizeClass, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, mergeProps as _mergeProps, createVNode as _createVNode, vShow as _vShow, withDirectives as _withDirectives, createTextVNode as _createTextVNode, withCtx as _withCtx, Fragment as _Fragment, createElementBlock as _createElementBlock, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-39c1c99c"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "text-sm" }
const _hoisted_2 = { class: "flex justify-end" }

import AButton from "@/components/Base/AButton";
import { DatetimeHighlightCallback, IonDatetimeCustomEvent } from '@ionic/core';
import { DatetimeChangeEventDetail, IonDatetime, IonIcon, IonPopover } from '@ionic/vue';
import { caretDownOutline } from "ionicons/icons";
import moment from 'moment';
import { computed, nextTick, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

interface DateRangePickerProps {
  start?: string;
  end?: string;
  placeholder?: string;
  offsetY?: number;
  hiddenPopover?: boolean;
  showIcon?: boolean;
  showBackdrop?: boolean;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'DateRangePicker',
  props: {
    start: { default: "" },
    end: { default: "" },
    placeholder: { default: "" },
    offsetY: { default: 0 },
    hiddenPopover: { type: Boolean, default: false },
    showIcon: { type: Boolean, default: false },
    showBackdrop: { type: Boolean, default: true }
  },
  emits: ["update:range", "update:single", "didDismiss"],
  setup(__props: any, { emit }) {

const props = __props;

const { t, locale } = useI18n({ useScope: 'global' });




const startDate = ref<string>(props.start ?? new Date().toISOString());
const endDate = ref<string>(props.end ?? new Date().toISOString());
const isStartDatesTurn = ref(true);

const range = ref<string[]>([]);
// used for "May 2021 workaround". If empty array is passed to ion-datetime, it will not show current date but May 2021
// workaround can be removed after ionic upgrade to https://github.com/ionic-team/ionic-framework/releases/tag/v8.3.1
const defaultRange = ref<string[] | null>(null);

const isOpen = ref(false);
const popoverEvent = ref();
const popoverElement = ref<null | typeof IonPopover>(null);

const noFilterLabel = computed(() => props.placeholder || t("hzba.buttons.datefilter"));

const format = (date: string) => {
  return date === "" || !moment(date).isValid() ? "" : moment(date).format("YYYY-MM-DD");
};

watch([startDate, endDate], (value, oldValue) => {
  // do not update range if values are the same, changed from computed to watch because computed always constructs new array object
  if (JSON.stringify(value) === JSON.stringify(oldValue)) return;
  range.value = value.filter(x => x !== "");
});

watch([() => props.offsetY, popoverElement], ([offset, element]) => {
  if(element) {
    element.$el.style.setProperty("--offset-y", `${offset}px`);
  }
});

watch(isOpen, (value, oldValue) => {
  if (!value && oldValue) {
    emit("didDismiss");
  }
});

const buttonText = computed(() => {
  const formatting = locale.value === 'de' ? 'DD.MM.YY' : 'MM-DD-YY';
  if (startDate.value && endDate.value) {
    return `${moment(startDate.value).format(formatting)} - ${moment(endDate.value).format(formatting)}`;
  } else if (startDate.value) {
    return `${moment(startDate.value).format(formatting)}`;
  } else {
    return noFilterLabel.value;
  }
});

const helpText = computed(() => {
  if (!startDate.value || !endDate.value) {
    return isStartDatesTurn.value ? t("hzba.datefilter.selectStartDate") : t("hzba.datefilter.selectEndDate");
  } else {
    return "";
  }
});

watch(() => props.start, (start) => {
  if (!start) {
    startDate.value = "";
    endDate.value = "";
  }
});

const unsubscribeDefaultRange = watch(range, (value) => {
  if (value.length === 0 && !isOpen.value) {
    // May 2021 workaround: if range is empty and popover not shown, set defaultRange to current date
    defaultRange.value = [new Date().toISOString()];
  }
}, { immediate: true });

const onWillPresent = () => {
  // May 2021 workaround: when popover is presented the current date is removed
  // but ion-datetime will remember the right view and not jump back to May 2021
  defaultRange.value = null;
  unsubscribeDefaultRange(); // watcher only needed initially 
};

const emitEvents = () => {
  if (startDate.value && endDate.value) {
    emit('update:range', [format(startDate.value), format(endDate.value)]);
  } else {
    emit('update:single', format(startDate.value));
  }
};

const openPopover = (e: Event) => {
  popoverEvent.value = e;
  isOpen.value = true;
};

const cancel = () => {
  startDate.value = props.start;
  endDate.value = props.end;
  // May 2021 workaround: hide popover only in nextTick, so the defaultRange is not set to current date in current tick
  nextTick(() => isOpen.value = false);
};

const confirm = () => {
  emitEvents();
  isOpen.value = false;
};

const removeFilter = () => {
  startDate.value = "";
  endDate.value = "";
  emitEvents();
  // May 2021 workaround: hide popover only in nextTick, so the defaultRange is not set to current date in current tick
  nextTick(() => isOpen.value = false);
};

const defineHighlightStyle: DatetimeHighlightCallback = (date: string) => {
  let isDateWithinRange = false;
  if (moment(date).isAfter(startDate.value) && moment(date).isBefore(endDate.value)) {
    isDateWithinRange = true;
  }

  // TODO: get theme colors
  if (isDateWithinRange)
    return {
      backgroundColor: '#4818f717',
    };
};

const handleDateChange = (
  e: IonDatetimeCustomEvent<DatetimeChangeEventDetail>
) => {
  const selectedValues = e.detail.value as string[] | undefined;
  const length = selectedValues?.length ?? 0;
  const isNewSelection = !startDate.value && length > 0 || !endDate.value && length > 1 || length > 2;
  let isAlreadySetThisTurn = false;
  let newDate;
  if (isNewSelection && selectedValues) {
    newDate = selectedValues[length - 1];
  }

  // selecting startDate, if selected date is after endDate, switch them
  if (isNewSelection && newDate && isStartDatesTurn.value) {
    if (endDate.value && moment(newDate).isAfter(endDate.value)) {
      startDate.value = endDate.value; // switch
      endDate.value = newDate;
    } else {
      startDate.value = newDate;
    }
    isAlreadySetThisTurn = true;
    isStartDatesTurn.value = false;
  }

  // selecting endDate, if selected date is before startDate, switch them
  else if (isNewSelection && newDate && !isStartDatesTurn.value && !isAlreadySetThisTurn) {
    if (moment(newDate).isAfter(startDate.value)) {
      isStartDatesTurn.value = true;
      endDate.value = newDate;
    } else {
      endDate.value = startDate.value; // switch
      startDate.value = newDate;
    }
  }

  // if only one date is selected. needs to be inferred because selectedValues are missing the actually clicked date
  else if (startDate.value && endDate.value) {
    const foundStart = selectedValues?.find(x => x === startDate.value);
    const foundEnd = selectedValues?.find(x => x === endDate.value);

    if (!foundStart || !foundEnd) {
      startDate.value = foundStart ? endDate.value : startDate.value; // infer the clicked date
      endDate.value = "";
      isStartDatesTurn.value = false;
    }
  }
  // if all dates are removed
  else if (length === 0) {
    startDate.value = "";
    endDate.value = "";
    isStartDatesTurn.value = true;
  }
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", _mergeProps(_ctx.$attrs, {
      class: buttonText.value !== noFilterLabel.value ? 'active' : '',
      onClick: openPopover
    }), [
      _createElementVNode("span", null, _toDisplayString(buttonText.value), 1),
      (props.showIcon)
        ? (_openBlock(), _createBlock(_unref(IonIcon), {
            key: 0,
            icon: _unref(caretDownOutline),
            class: _normalizeClass(["ml-1 transform transition-transform duration-200", isOpen.value ? 'highlighted rotate-180 ': ''])
          }, null, 8, ["icon", "class"]))
        : _createCommentVNode("", true)
    ], 16),
    _createVNode(_unref(IonPopover), {
      ref_key: "popoverElement",
      ref: popoverElement,
      class: _normalizeClass(["ion-datetime-button-overlay", { hidden: props.hiddenPopover }]),
      "is-open": isOpen.value,
      event: popoverEvent.value,
      "show-backdrop": props.showBackdrop,
      "keep-contents-mounted": true,
      onWillPresent: onWillPresent,
      onDidDismiss: _cache[3] || (_cache[3] = ($event: any) => (cancel()))
    }, {
      default: _withCtx(() => [
        _createVNode(_unref(IonDatetime), {
          multiple: "",
          presentation: "date",
          "prefer-wheel": false,
          locale: _unref(locale),
          value: defaultRange.value ?? range.value,
          "first-day-of-week": _unref(locale) === 'de' ? 1 : 0,
          "highlighted-dates": defineHighlightStyle,
          onIonChange: handleDateChange
        }, null, 8, ["locale", "value", "first-day-of-week"]),
        _withDirectives(_createElementVNode("div", _hoisted_1, [
          _createElementVNode("ul", null, [
            _createElementVNode("li", null, _toDisplayString(helpText.value), 1)
          ])
        ], 512), [
          [_vShow, helpText.value]
        ]),
        _createElementVNode("div", _hoisted_2, [
          _createVNode(_unref(AButton), {
            class: "p-3 bg-transparent",
            onClick: _cache[0] || (_cache[0] = ($event: any) => (cancel()))
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(_ctx.$t("hzba.buttons.abbrechen")), 1)
            ]),
            _: 1
          }),
          _withDirectives(_createVNode(_unref(AButton), {
            class: "p-3 bg-transparent",
            onClick: _cache[1] || (_cache[1] = ($event: any) => (removeFilter()))
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(_ctx.$t("hzba.buttons.removeFilter")), 1)
            ]),
            _: 1
          }, 512), [
            [_vShow, props.start]
          ]),
          _withDirectives(_createVNode(_unref(AButton), {
            class: "p-3 mr-2 bg-transparent",
            onClick: _cache[2] || (_cache[2] = ($event: any) => (confirm()))
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(_ctx.$t("hzba.buttons.bestaetigen")), 1)
            ]),
            _: 1
          }, 512), [
            [_vShow, range.value.length > 0]
          ])
        ])
      ]),
      _: 1
    }, 8, ["class", "is-open", "event", "show-backdrop"])
  ], 64))
}
}

})